import { useAuth0 } from '@auth0/auth0-vue';
import { userStore } from '@/stores/UserStore';
import { AppClaims, AppRoles } from '@/enums/ApplicationClaims.enum';
import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';

interface Claim {
  key: string;
  value: string;
}

// Validate user and claims
function validateUserAndClaims(
  user: any,
  userInfo: ReturnType<typeof userStore>
): boolean {
  if (!user || !user['https://booster.co.nz/claims']) {
    userInfo.showError(
      'Access denied. No permission to access this application.'
    );
    return false;
  }
  return true;
}

// Extract roles from claims
function getRolesFromClaims(claims: Claim[]): string[] {
  return claims
    .filter((claim) => claim.key === AppClaims.AppRolesClaim)
    .flatMap((claim) => claim.value.split(','));
}

// Check if any required role is present
function hasRequiredRole(
  roles: string[],
  requiredRoles: string[],
  userInfo: ReturnType<typeof userStore>
): boolean {
  if (!roles.length || !requiredRoles.some((role) => roles.includes(role))) {
    userInfo.showError(
      'Access denied. Your role does not permit access to this application.'
    );
    return false;
  }
  return true;
}

// Extract and set adviser details
function setAdviserDetails(
  claims: Claim[],
  userInfo: ReturnType<typeof userStore>
): boolean {
  const adviserExternalReference = claims.find(
    (claim) => claim.key === AppClaims.AppExternalRefClaim
  );
  if (!adviserExternalReference || !adviserExternalReference.value) {
    userInfo.showError(
      'Access denied. ExternalReference/AdviserId is missing.'
    );
    return false;
  }

  const currentAdviserId = Number(adviserExternalReference.value);
  const adviserName = claims.find(
    (claim) => claim.key === 'display_name'
  )?.value;

  if (!adviserName) {
    userInfo.showError('Access denied. Adviser name is missing.');
    return false;
  }

  userInfo.setAdviserId(currentAdviserId);
  userInfo.setUserName(adviserName);
  return true;
}

// Generic auth guard for specified roles
function createRoleAuthGuard(requiredRoles: string[]): () => boolean | Error {
  return function checkRoleAuthGuard() {
    const userInfo = userStore();
    const authService = useAuth0();
    const user = authService.user.value;
    try {
      if (!validateUserAndClaims(user, userInfo)) {
        return false;
      }

      const claims = user && (user['https://booster.co.nz/claims'] as Claim[]);

      if (!claims) {
        userInfo.showError('Access denied. Claims not found.');
        return false;
      }

      const roles = getRolesFromClaims(claims);

      if (!hasRequiredRole(roles, requiredRoles, userInfo)) {
        return false;
      }

      if (!setAdviserDetails(claims, userInfo)) {
        return false;
      }

      return true;
    } catch (error) {
      userInfo.showError(
        'An error occurred while checking your access. Please try again later.'
      );
      return new Error(`Error: ${error}`);
    }
  };
}

/**
 * Auth guard for DefaultAdviser|InsuranceAdviser|Investment roles.
 * Allows access if the user has either role.
 */
export const defaultAdviserAuthGuard = createRoleAuthGuard([
  AppRoles.InsuranceAdviser,
  AppRoles.DefaultAdviser,
  AppRoles.InvestmentAdviser,
]);

/**
 * Auth guard for InsuranceAdviser role.
 * Allows access if the user has the InsuranceAdviser role.
 */
export const insuranceAdviserAuthGuard = createRoleAuthGuard([
  AppRoles.InsuranceAdviser,
]);

/**
 * Auth guard for InsuranceAdviser role.
 * Allows access if the user has the InsuranceAdviser role.
 */
export const investmentAdviserAuthGuard = createRoleAuthGuard([
  AppRoles.InvestmentAdviser,
]);

/**
 * Wrapper guard to redirect to /tools-forms-docs if insuranceAdviserAuthGuard fails.
 */
//TODO: Check the changes needed with insurance once tools-forms-docs is also implemented in Investments
export function insuranceAdviserAuthGuardWithRedirect(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const result = insuranceAdviserAuthGuard();
  if (result === true) {
    return next(); // Guard passed
  }
  if (result instanceof Error) {
    return next(result); // Propagate error
  }
  next('/tools-forms-docs'); // Guard failed (false), redirect
}

/**
 * Guard to allow access if user is either InsuranceAdviser or InvestmentAdviser.
 * Redirects to error page if neither.
 */
export function adviserProductAccessGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const insuranceAccess = insuranceAdviserAuthGuard();
  const investmentAccess = investmentAdviserAuthGuard();
  if (insuranceAccess === true || investmentAccess === true) {
    next();
  } else {
    next('/error');
  }
}
